// ----------------------------------------------------------------------
// Ask AI (epic #391) — pure helpers for the drawer: what page the teacher is on, the
// starter prompts for it, the per-class transcript in localStorage, and the slice of it
// sent to the agent.
// ----------------------------------------------------------------------

import type { AssistantActionCard, AssistantStreamEvent, ExecuteAssistantActionResult } from '@classroom-ai/types';

/** A proposed action plus what happened when the teacher acted on it. */
export type AskAiAction = AssistantActionCard & {
  outcome?: ExecuteAssistantActionResult;
  failed?: string;
  /** The background job running it (Phase G) — polling resumes after a reload. */
  jobId?: string;
  running?: boolean;
  /** Set once the teacher undid a finished action. */
  revertedMessage?: string;
};

/** Cards a "Do all" would run: runnable, not done/running, nothing to overwrite. */
export function batchable(actions: AskAiAction[] | undefined): AskAiAction[] {
  return (actions ?? []).filter((a) => a.runnable && !a.existing && !a.running && a.outcome?.status !== 'created');
}

export type AskAiMessage = { role: 'user' | 'assistant'; content: string; error?: boolean; actions?: AskAiAction[] };

/**
 * Mirrors the agent's `viewContext` input. A hint only — the agent re-verifies ids.
 * `scope`: the Standards page's Course plan / class toggle (epic #518), or the Lesson
 * Plans board's Schedule picker — `course` when it shows the baseline (epic #527).
 */
export type AskAiViewContext = {
  route: 'lesson' | 'unit' | 'standards' | 'lesson-plans' | 'student-sessions' | 'page';
  lessonId?: string;
  unitId?: string;
  scope?: 'course' | 'class';
  /** Lesson Plans board, class scope: the section is another teacher's (chip wording only). */
  othersClass?: boolean;
  /** A student's Sessions page (epic #547): the student and the open session. */
  studentUid?: string;
  sessionId?: string;
  /** Chip wording only. */
  studentName?: string;
};

const LESSON_ROUTE = /^\/dashboard\/lesson-plans\/([^/]+)\/([^/]+)\/?$/;
const UNIT_ROUTE = /^\/dashboard\/lesson-plans\/([^/]+)\/?$/;

/** The agent's view context for the current pathname (pages may register more: useAskAiPageView). */
export function viewContextFor(pathname: string): AskAiViewContext {
  const lesson = LESSON_ROUTE.exec(pathname);
  if (lesson) return { route: 'lesson', unitId: lesson[1], lessonId: lesson[2] };
  const unit = UNIT_ROUTE.exec(pathname);
  if (unit) return { route: 'unit', unitId: unit[1] };
  return { route: 'page' };
}

const GENERAL_PROMPTS = [
  'Are the next 5 lessons ready to teach?',
  "What's on the schedule for the next week?",
  'How is the class doing? Any open alerts?',
  'Which standards are students struggling with?',
];

const LESSON_PROMPTS = [
  'Is this lesson ready to teach?',
  "Does this lesson's Exit Ticket have a quiz?",
  "How did students do on this lesson's practice?",
  'Are the next 5 lessons ready to teach?',
];

// Standards page (epic #518): questions that join coverage with mastery and pacing —
// the agent's get_standards_coverage answers each with one filter.
export const STANDARDS_PROMPTS = [
  'Which standards are at risk before the next unit test?',
  'Where is coverage not turning into mastery?',
  'What am I behind on, and what can I compress or skip?',
  'Fill my thinnest standards',
];

// Course plan view: the course-wide gaps question replaces the class's "behind" one.
export const STANDARDS_COURSE_PROMPTS = [
  "What's missing from the course plan?",
  'Which standards are at risk before the next unit test?',
  'Where is coverage not turning into mastery?',
  'Fill my thinnest standards',
];

// Lesson Plans board (epic #527): the agent's get_lesson_plans_board answers each with one view.
export const LESSON_PLANS_PROMPTS = [
  "Which lessons in this unit aren't ready to teach, and what's missing?",
  'Which lessons scored low in every section, and which only in my class?',
  'Will I finish this unit before the unit test?',
  'Which lessons have I taught that have no exit ticket results?',
];

/** The same class chips for a Department Head viewing another teacher's section. */
export const LESSON_PLANS_OTHERS_PROMPTS = [
  "Which lessons in this unit aren't ready to teach, and what's missing?",
  'Which lessons scored low in every section, and which only in this class?',
  'Will this class finish this unit before the unit test?',
  'Which lessons has this class been taught that have no exit ticket results?',
];

// Course order (baseline) view, for Department Heads: the whole course plan across sections.
export const LESSON_PLANS_COURSE_PROMPTS = [
  'Which units are least ready to teach?',
  'Which lessons are low in every section?',
  'How far apart are the sections on pace?',
  'Which upcoming lessons are still only planned, not written?',
];

/** A student's Sessions page (epic #547): the score explainer's questions, by name. */
export function studentSessionPrompts(name: string): string[] {
  return [
    `Why did ${name}’s scores change in this session?`,
    `Which answers moved ${name}’s scores the most?`,
    `Explain ${name}’s score for a parent`,
    `How is ${name} doing overall?`,
  ];
}

/** Starter chips shown on an empty transcript, by page. */
export function starterPrompts(view: AskAiViewContext): string[] {
  if (view.route === 'student-sessions') return studentSessionPrompts(view.studentName ?? 'this student');
  if (view.route === 'lesson') return LESSON_PROMPTS;
  if (view.route === 'standards') return view.scope === 'course' ? STANDARDS_COURSE_PROMPTS : STANDARDS_PROMPTS;
  if (view.route === 'lesson-plans') {
    if (view.scope === 'course') return LESSON_PLANS_COURSE_PROMPTS;
    return view.othersClass ? LESSON_PLANS_OTHERS_PROMPTS : LESSON_PLANS_PROMPTS;
  }
  return GENERAL_PROMPTS;
}

/** The drawer's empty-state line, by page. */
export function starterIntro(view: AskAiViewContext): string {
  if (view.route === 'student-sessions') {
    return `Ask how ${view.studentName ?? 'this student'}’s skill scores were calculated — which answers moved them and why — or get a summary for a parent.`;
  }
  if (view.route === 'standards') {
    return 'Ask how your lesson plans cover each standard — what is at risk, behind or thin, and where coverage is not turning into mastery.';
  }
  if (view.route === 'lesson-plans') {
    return view.scope === 'course'
      ? 'Ask about the course plan across every section — which units are least ready, which lessons are low everywhere, and how far apart the sections are.'
      : view.othersClass
        ? "Ask about this class's lesson plans — what is not ready, which lessons scored low, and whether this unit fits before its test."
        : 'Ask about your lesson plans — what is not ready, which lessons scored low, and whether this unit fits before its test.';
  }
  return "Ask about this class — upcoming lessons, whether they're ready to teach, alerts and reviews.";
}

// Per-class transcript, per browser — like RefinementChat's per-lesson history. Bounded so
// long chats can't blow the localStorage quota.
const STORAGE_PREFIX = 'askAi:v1:';
export const MAX_STORED = 50;

export function loadTranscript(classId: string): AskAiMessage[] {
  try {
    const parsed = JSON.parse(localStorage.getItem(STORAGE_PREFIX + classId) ?? 'null');
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function saveTranscript(classId: string, messages: AskAiMessage[]): void {
  try {
    if (messages.length === 0) localStorage.removeItem(STORAGE_PREFIX + classId);
    else localStorage.setItem(STORAGE_PREFIX + classId, JSON.stringify(messages.slice(-MAX_STORED)));
  } catch {
    // localStorage unavailable (private mode) or over quota — transcript stays in memory.
  }
}

/** What the agent accepts: ≤ 50 messages, ≤ 8000 chars each. Older context matters less. */
const MAX_SENT = 20;
const MAX_CHARS = 8000;

/** The conversation to send: recent turns only, failed replies dropped, long ones clipped. */
export function requestMessages(messages: AskAiMessage[]): { role: 'user' | 'assistant'; content: string }[] {
  const out = messages
    .filter((m) => !m.error && m.content.trim())
    .slice(-MAX_SENT)
    .map((m) => ({ role: m.role, content: m.content.slice(0, MAX_CHARS) }));
  // The agent wants the conversation to start from a question.
  while (out.length && out[0].role !== 'user') out.shift();
  return out;
}

// ----------------------------------------------------------------------
// Streaming (#405): the in-progress reply the drawer shows while the agent works.
// ----------------------------------------------------------------------

/** One status line ("Checking 5 lessons' readiness…"); `done` once the next step began. */
export type AskAiStep = { label: string; done: boolean };

export type AskAiLive = {
  steps: AskAiStep[];
  /** The model is composing (no tool running, no text yet). */
  thinking: boolean;
  /** Provisional answer text — replaced by `done.answer`, dropped by `retract`. */
  text: string;
};

export const EMPTY_LIVE: AskAiLive = { steps: [], thinking: true, text: '' };

/** Fold one stream event into the live view. `done` / `error` are the drawer's to handle. */
export function applyStreamEvent(live: AskAiLive, event: AssistantStreamEvent): AskAiLive {
  switch (event.type) {
    case 'tool':
      return { ...live, thinking: false, steps: [...live.steps.map((s) => ({ ...s, done: true })), { label: event.status, done: false }] };
    case 'thinking':
      return { ...live, thinking: true, text: '', steps: live.steps.map((s) => ({ ...s, done: true })) };
    case 'text':
      return { ...live, thinking: false, text: live.text + event.text };
    case 'retract':
      return { ...live, text: '' };
    default:
      return live;
  }
}
